import type DOMPurify from 'dompurify';
import { useMemo, useRef, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';

import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import { EvErrorBoundary } from 'hb-react/shared/components/ErrorBoundary';

import { useHtmlContentProcessor } from './replacementEngine';
import { DivWithEmailStyles, DivWithWebStyles } from './ui';
import { sanitizeHtml } from './utils';

interface HtmlContentProps {
  config?: DOMPurify.Config;
  content: string;
  dataTestId?: string;
  enableImagePreview?: boolean;
  stylesConfig?: CSSProperties | 'email' | 'web';
}

export const HtmlContent = ({
  content,
  config,
  enableImagePreview = false,
  stylesConfig,
  dataTestId,
}: HtmlContentProps) => {
  const [HtmlDiv, stylesConfigIsCustomCss] = useMemo(() => {
    if (stylesConfig === 'web') {
      return [DivWithWebStyles, false] as const;
    }
    if (stylesConfig === 'email') {
      return [DivWithEmailStyles, false] as const;
    }

    return [null, !!stylesConfig] as const;
  }, [stylesConfig]);

  const { currentNetwork } = useDefaultAppContexts();
  const containerRef = useRef<HTMLDivElement>(null);

  const displayThirdPartyConsent = useMemo(
    () =>
      currentNetwork?.features?.cookies?.config?.display_third_party_consent ||
      false,
    [currentNetwork],
  );

  const sanitizedContent = useMemo(
    () => sanitizeHtml(content, config),
    [content, config],
  );

  const { dangerouslySetInnerHTML, portals } = useHtmlContentProcessor({
    content: sanitizedContent,
    displayThirdPartyConsent,
    enableImagePreview,
    containerRef,
  });

  return (
    <EvErrorBoundary>
      {HtmlDiv ? (
        <HtmlDiv
          data-testid={dataTestId}
          ref={containerRef}
          /* eslint-disable-next-line react/no-danger */
          dangerouslySetInnerHTML={dangerouslySetInnerHTML}
        />
      ) : (
        <div
          style={
            stylesConfigIsCustomCss
              ? (stylesConfig as CSSProperties)
              : undefined
          }
          ref={containerRef}
          /* eslint-disable-next-line react/no-danger */
          dangerouslySetInnerHTML={dangerouslySetInnerHTML}
          data-testid={dataTestId}
        />
      )}
      {portals.map(({ element, target, key }) =>
        createPortal(element, target, key),
      )}
    </EvErrorBoundary>
  );
};

const withIframeConfig = {
  ADD_TAGS: ['iframe'],
  ADD_ATTR: ['target', 'allow', 'allowfullscreen', 'frameborder', 'scrolling'],
};

export const HtmlContentWithIframe = ({
  content,
  enableImagePreview = false,
  stylesConfig,
}: Omit<HtmlContentProps, 'config'>) => (
  <HtmlContent
    content={content}
    config={withIframeConfig}
    enableImagePreview={enableImagePreview}
    stylesConfig={stylesConfig}
  />
);

// Required for app/assets/javascripts/initializers/react/components/shared.js
export default HtmlContent;
